import React, { useEffect, useState } from "react";
import moment from "moment";
import { Clock, User, CheckCircle, XCircle, MessageSquare, Calendar, AlertTriangle, Bug, UserCheck, UserX } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import { api } from "../../../../services/api";

interface DetalheRelatorio {
  id: string;
  inicio_treinamento: string;
  termino_treinamento: string;
  treinamento: Treinamento;
  oficial_responsavel: string;
  criado_em: string;
  patente_treinador: string;
  treinador: Militar;
  patente_auxiliar: string;
  auxiliar: Militar;
  sala: string;
  aprovados: Aprovados[];
  reprovados: Reprovados[];
  qtd_treinados: number;
  qtd_aprovados: number;
  observacao: string;
}

interface Treinamento {
  treinamento: string;
}

interface Militar {
  nick: string;
}

interface Aprovados {
  nick: string;
}

interface Reprovados {
  nick: string;
  motivo: string;
  horario: string;
}

interface ErroRelatorio {
  id: string;
  descricao: string;
  status: "aberto" | "visualizado";
  criado_em: string;
  militar: {
    nick: string;
  };
}

export function DetalheRelatorio({
  id,
  inicio_treinamento,
  termino_treinamento,
  treinamento,
  oficial_responsavel,
  criado_em,
  patente_treinador,
  treinador,
  patente_auxiliar,
  auxiliar,
  sala,
  aprovados,
  reprovados,
  qtd_treinados,
  qtd_aprovados,
  observacao,
}: DetalheRelatorio) {
  const [erros, setErros] = useState<ErroRelatorio[]>([]);
  const [carregandoErros, setCarregandoErros] = useState(true);
  const [nicksRegistrados, setNicksRegistrados] = useState<Set<string>>(new Set());
  const [verificandoNicks, setVerificandoNicks] = useState(true);

  useEffect(() => {
    async function carregarErros() {
      try {
        setCarregandoErros(true);
        const response = await api.get(`/treinamentos/relatorios/${id}/erros`);
        setErros(response.data);
      } catch (error) {
        console.error("Erro ao carregar erros do relatório:", error);
        setErros([]);
      } finally {
        setCarregandoErros(false);
      }
    }

    async function verificarNicks() {
      const todosNicks = [
        ...aprovados.map((a) => a.nick),
        ...reprovados.map((r) => r.nick),
      ];
      if (todosNicks.length === 0) {
        setVerificandoNicks(false);
        return;
      }
      try {
        setVerificandoNicks(true);
        const response = await api.post("/militares/verificarNicks", { nicks: todosNicks });
        setNicksRegistrados(new Set<string>(response.data.registrados));
      } catch (err) {
        console.error("Erro ao verificar nicks:", err);
      } finally {
        setVerificandoNicks(false);
      }
    }

    carregarErros();
    verificarNicks();
  }, [id]);



  const obterCorStatus = (status: string) => {
    switch (status) {
      case "aberto":
        return "bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-700";
      case "visualizado":
        return "bg-green-100 text-green-800 border-green-300 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600";
    }
  };
  return (
    <Card className="bg-gradient-to-br from-[oklch(0.95_0.05_142)] to-[oklch(0.98_0.02_142)] dark:from-[oklch(0.18_0.03_142)] dark:to-[oklch(0.22_0.02_142)] border-[oklch(0.45_0.15_142)]">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold text-[oklch(0.35_0.15_142)] dark:text-[oklch(0.75_0.15_142)] flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Relatório Detalhado de Treinamento
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Informações Básicas */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="space-y-0.5">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Calendar className="h-3.5 w-3.5 text-[oklch(0.45_0.15_142)]" />
              <span className="font-medium">Data</span>
            </div>
            <p>{moment(criado_em).format("DD/MM/YYYY")}</p>
          </div>
          <div className="space-y-0.5">
            <div className="flex items-center gap-1 text-muted-foreground">
              <User className="h-3.5 w-3.5 text-[oklch(0.45_0.15_142)]" />
              <span className="font-medium">Oficial Responsável</span>
            </div>
            <p>{oficial_responsavel}</p>
          </div>
          <div className="space-y-0.5">
            <p className="font-medium text-muted-foreground">Treinador</p>
            <p>{patente_treinador} {treinador.nick}</p>
          </div>
          {auxiliar && (
            <div className="space-y-0.5">
              <p className="font-medium text-muted-foreground">Auxiliar</p>
              <p>{patente_auxiliar} {auxiliar.nick}</p>
            </div>
          )}
        </div>

        {/* Informações do Treinamento */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
          <h4 className="font-semibold text-[oklch(0.35_0.15_142)] dark:text-[oklch(0.75_0.15_142)] mb-3">
            {treinamento.treinamento} - Sala {sala}
          </h4>
          <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <p>
              • Às [{moment(inicio_treinamento).format("HH:mm")}] o{" "}
              {treinamento.treinamento} deu início com {qtd_treinados} treinando
              {qtd_treinados > 1 && "s"}.
            </p>

            {reprovados.map(({ nick, horario, motivo }, index) => (
              <div key={index} className="space-y-1">
                <p>
                  • Às [{moment(horario, "HH:mm:ss").format("HH:mm")}] o treinando{" "}
                  <span className="font-medium text-red-600">{nick}</span> foi reprovado.
                </p>
                <p className="text-red-600 ml-4">Motivo: {motivo}</p>
              </div>
            ))}

            {reprovados.length === 0 && (
              <p className="text-green-600">• Este treinamento não teve reprovados.</p>
            )}

            <p>
              • Às [{moment(termino_treinamento).format("HH:mm")}] o{" "}
              {treinamento.treinamento} teve seu fim com {qtd_aprovados}{" "}
              {qtd_aprovados === 1 ? "aprovado" : "aprovados"}.
            </p>
          </div>
        </div>

        {/* Lista de Participantes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Aprovados */}
          {aprovados.length > 0 && (
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-700">
              <h4 className="font-semibold text-green-800 dark:text-green-300 mb-3 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Aprovados ({aprovados.length})
              </h4>
              <div className="space-y-1">
                {aprovados.map(({ nick }, index) => (
                  <div key={index} className="flex items-center gap-1.5">
                    <Badge className="bg-green-100 text-green-800 border-green-300 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700 text-xs">
                      {nick}
                    </Badge>
                    {!verificandoNicks && (
                      nicksRegistrados.has(nick.toLowerCase().trim())
                        ? <span title="Registrado no site"><UserCheck className="h-3.5 w-3.5 text-green-600 shrink-0" /></span>
                        : <span title="Não registrado no site"><UserX className="h-3.5 w-3.5 text-orange-500 shrink-0" /></span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reprovados */}
          {reprovados.length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-700">
              <h4 className="font-semibold text-red-800 dark:text-red-300 mb-3 flex items-center gap-2">
                <XCircle className="h-4 w-4" />
                Reprovados ({reprovados.length})
              </h4>
              <div className="space-y-1">
                {reprovados.map(({ nick }, index) => (
                  <div key={index} className="flex items-center gap-1.5">
                    <Badge className="bg-red-100 text-red-800 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700 text-xs">
                      {nick}
                    </Badge>
                    {!verificandoNicks && (
                      nicksRegistrados.has(nick.toLowerCase().trim())
                        ? <span title="Registrado no site"><UserCheck className="h-3.5 w-3.5 text-green-600 shrink-0" /></span>
                        : <span title="Não registrado no site"><UserX className="h-3.5 w-3.5 text-orange-500 shrink-0" /></span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Observações */}
        {observacao && (
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
            <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-2 flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Observações
            </h4>
            <p className="text-blue-700 dark:text-blue-300 text-sm leading-relaxed">{observacao}</p>
          </div>
        )}

        {/* Erros Reportados */}
        <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-700">
          <h4 className="font-semibold text-orange-800 dark:text-orange-300 mb-3 flex items-center gap-2">
            <Bug className="h-4 w-4" />
            Erros Reportados
            {!carregandoErros && (
              <Badge className="bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-700 text-xs">
                {erros.length}
              </Badge>
            )}
          </h4>

          {carregandoErros ? (
            <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600"></div>
              <span className="text-sm text-orange-600 dark:text-orange-400">Carregando erros...</span>
            </div>
          ) : erros.length === 0 ? (
            <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400">
              <CheckCircle className="h-4 w-4" />
              <span className="text-sm">Nenhum erro foi reportado para este relatório.</span>
            </div>
          ) : (
            <div className="space-y-3">
              {erros.map((erro) => (
                <div key={erro.id} className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-orange-200 dark:border-orange-700 shadow-sm">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1">
                      <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">{erro.descricao}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge className={`text-xs ${obterCorStatus(erro.status)}`}>
                        {erro.status === "aberto" ? "Aberto" : "Visualizado"}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      Reportado por: {erro.militar.nick}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {moment(erro.criado_em).format("DD/MM/YYYY HH:mm")}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
